import { HygienistCourse } from "@/api";

const COURSE_RESET_ROLES = new Set([0, 1]);
const COMPLETED_STATUSES = new Set(["completed", "passed"]);

export const isCurrentCourseTalent = (
  selectedHygienistId: number | null | undefined,
  requestedHygienistId: number | null | undefined
): boolean =>
  Number.isSafeInteger(selectedHygienistId) &&
  Number.isSafeInteger(requestedHygienistId) &&
  selectedHygienistId === requestedHygienistId;

export const clearCourseSelectionForTalentSwitch = (context: {
  courses: HygienistCourse[];
  loadingCourses: boolean;
  resettingCourseCode: string;
  courseRequestId: number;
  courseResetRequestId: number;
  courseResetDialog: {
    open: boolean;
    course: HygienistCourse | null;
    hygienistId: number | null;
  };
}): void => {
  context.courseRequestId += 1;
  context.courseResetRequestId += 1;
  context.courses = [];
  context.loadingCourses = false;
  context.resettingCourseCode = "";
  context.courseResetDialog = {
    open: false,
    course: null,
    hygienistId: null
  };
};

export type CourseLoadResult =
  | { state: "applied" }
  | { state: "stale" }
  | { state: "error"; error: unknown };

export const loadCoursesForSelectedTalent = async (
  context: {
    courses: HygienistCourse[];
    loadingCourses: boolean;
    courseRequestId: number;
  },
  requestedHygienistId: number,
  selectedHygienistId: () => number | null | undefined,
  fetchCourses: () => Promise<HygienistCourse[]>
): Promise<CourseLoadResult> => {
  const requestId = ++context.courseRequestId;
  context.loadingCourses = true;
  const isCurrent = () =>
    requestId === context.courseRequestId &&
    isCurrentCourseTalent(selectedHygienistId(), requestedHygienistId);
  try {
    const courses = await fetchCourses();
    if (!isCurrent()) return { state: "stale" };
    context.courses = courses;
    return { state: "applied" };
  } catch (error) {
    if (!isCurrent()) return { state: "stale" };
    context.courses = [];
    return { state: "error", error };
  } finally {
    if (isCurrent()) context.loadingCourses = false;
  }
};

export const canResetCourseProgress = (
  adminRole: number | null,
  course: HygienistCourse
): boolean =>
  Number.isInteger(adminRole) &&
  COURSE_RESET_ROLES.has(adminRole as number) &&
  course.requirementType === "required" &&
  !!course.code &&
  COMPLETED_STATUSES.has(String(course.status).toLowerCase());

const asUtcDate = (value: string): Date => {
  const normalized = /(?:z|[+-]\d\d:\d\d)$/i.test(value)
    ? value
    : `${value.replace(" ", "T")}Z`;
  return new Date(normalized);
};

export const formatCourseCompletionDate = (value: string | null): string => {
  if (!value) return "";
  const parsed = asUtcDate(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "America/Toronto"
  }).format(parsed);
};
